import { Link, Outlet, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  ClipboardList,
  Home,
  LogOut,
  Receipt,
  Users,
} from 'lucide-react';
import { appConfig } from '@/lib/app-config';
import { canApproveTeam } from '@/lib/roles';
import { useAuth } from '../store/auth';
import { useBranding } from '../store/branding';
import BottomNav, { type NavItem } from './ui/BottomNav';
import SidebarNav from './ui/SidebarNav';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import NotificationBell from './NotificationBell';

const employeeNavItems: NavItem[] = [
  { to: '/dashboard', label: 'Home', icon: Home },
  { to: '/attendance', label: 'Attendance', icon: ClipboardList },
  { to: '/leave', label: 'Leave', icon: CalendarDays },
  { to: '/claims', label: 'Claims', icon: Receipt },
];

const managerNavItems: NavItem[] = [
  { to: '/dashboard', label: 'Home', icon: Home },
  { to: '/attendance', label: 'Attendance', icon: ClipboardList },
  { to: '/leave', label: 'Leave', icon: CalendarDays },
  { to: '/claims', label: 'Approvals', icon: Receipt },
  { to: '/employees', label: 'Team', icon: Users },
];

function userInitials(name?: string | null, email?: string | null): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return '?';
}

function PartnerLogoStrip() {
  const base = import.meta.env.BASE_URL;
  return (
    <div className="flex items-center justify-center gap-6 border-b border-border bg-card px-4 py-2.5">
      <img src={`${base}partner-logos/plomooffshore.png`} alt="Plomo Offshore" className="h-6 w-auto opacity-80" />
      <img src={`${base}partner-logos/plomogroup.png`} alt="Plomogroup" className="h-8 w-auto opacity-90" />
      <img src={`${base}partner-logos/mydeck.png`} alt="MyDeck" className="h-5 w-auto opacity-80" />
    </div>
  );
}

function AppHeader({
  displayName,
  portalLabel,
  userName,
  userEmail,
  onLogout,
}: {
  displayName: string;
  portalLabel: string;
  userName?: string | null;
  userEmail?: string | null;
  onLogout: () => void;
}) {
  return (
    <header className="app-header sticky top-0 z-10 shrink-0">
      <div className="pt-safe">
        <div className="flex min-h-[3.25rem] items-center justify-between gap-3 px-4 py-2">
          <div className="min-w-0">
            <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-primary-foreground/70">
              {portalLabel}
            </p>
            <h1 className="truncate text-base font-semibold text-primary-foreground" title={displayName}>
              {displayName}
            </h1>
          </div>
          <div className="flex min-w-0 shrink items-center gap-2">
            <NotificationBell dark />
            <Link
              to="/profile"
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                'bg-primary-foreground/15 text-xs font-semibold text-primary-foreground ring-1 ring-primary-foreground/25'
              )}
              aria-label="My profile"
            >
              {userInitials(userName, userEmail)}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="shrink-0 gap-1 rounded-lg text-primary-foreground/90 hover:bg-primary-foreground/15 hover:text-primary-foreground"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>
      <PartnerLogoStrip />
    </header>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { displayName } = useBranding();
  const navigate = useNavigate();

  const isManager = user ? canApproveTeam(user.role) : false;
  const navItems = isManager ? managerNavItems : employeeNavItems;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (appConfig.isEmployeeWeb) {
    return (
      <div className="app-shell app-shell--web">
        <aside className="app-sidebar hidden md:flex">
          <div className="border-b border-border px-4 py-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {isManager ? 'Manager portal' : 'Employee portal'}
            </p>
            <h1 className="mt-1 truncate text-base font-semibold text-foreground" title={displayName}>
              {displayName}
            </h1>
          </div>
          <PartnerLogoStrip />
          <SidebarNav items={navItems} />
          <div className="mt-auto border-t border-border p-4">
            <div className="mb-3 flex items-center gap-3">
              <Link to="/profile" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary hover:bg-primary/20">
                {userInitials(user?.name, user?.email)}
              </Link>
              <Link to="/profile" className="min-w-0 flex-1 hover:underline">
                <p className="truncate text-sm font-medium text-foreground">{user?.name ?? user?.email}</p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              </Link>
              <NotificationBell dark={false} />
            </div>
            <Button variant="outline" size="sm" className="w-full gap-2" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="md:hidden">
            <AppHeader
              displayName={displayName}
              portalLabel={isManager ? 'Manager portal' : 'Employee portal'}
              userName={user?.name}
              userEmail={user?.email}
              onLogout={handleLogout}
            />
          </div>

          <main className="app-main-scroll app-main-scroll--web">
            <div className="mx-auto w-full max-w-3xl">
              <Outlet />
            </div>
          </main>

          <div className="md:hidden">
            <BottomNav items={navItems} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <AppHeader
        displayName={displayName}
        portalLabel={isManager ? 'Manager portal' : 'Employee portal'}
        userName={user?.name}
        userEmail={user?.email}
        onLogout={handleLogout}
      />

      <main className="app-main-scroll">
        <Outlet />
      </main>

      <BottomNav items={navItems} />
    </div>
  );
}
